export const OPERATIONS_AREAS = [
  {
    id: "execution",
    labelKey: "operations.areas.execution.label",
    descriptionKey: "operations.areas.execution.description",
    sectionSlugs: [
      "focus-inbox",
      "planning-verification",
      "form-activity",
      "submission-review",
      "review-queues",
      "caja",
      "tickets",
      "inspection-slas",
      "timeclock",
      "tracking",
      "reality-capture-review",
    ],
  },
  {
    id: "assets",
    labelKey: "operations.areas.assets.label",
    descriptionKey: "operations.areas.assets.description",
    sectionSlugs: [
      "map-drilldowns",
      "attribute-records",
      "evidence",
      "asset-intelligence",
      "digital-twin",
    ],
  },
  {
    id: "automation",
    labelKey: "operations.areas.automation.label",
    descriptionKey: "operations.areas.automation.description",
    sectionSlugs: [
      "forms",
      "catalogs",
      "workflows",
      "process-cloning",
      "templates",
    ],
  },
  {
    id: "intelligence",
    labelKey: "operations.areas.intelligence.label",
    descriptionKey: "operations.areas.intelligence.description",
    sectionSlugs: [
      "dashboard-daily-stats",
      "ventas",
      "insights",
      "reports",
      "context",
      "conversation-records",
      "search",
      "improvement-review",
    ],
  },
  {
    id: "data",
    labelKey: "operations.areas.data.label",
    descriptionKey: "operations.areas.data.description",
    sectionSlugs: [
      "export-center",
      "documents",
      "crm",
      "facturas",
      "data-pipeline",
    ],
  },
] as const;

export function getOperationsArea(areaId: string | undefined) {
  return OPERATIONS_AREAS.find((area) => area.id === areaId);
}

export function getOperationsAreaForSection(sectionSlug: string) {
  return OPERATIONS_AREAS.find((area) =>
    (area.sectionSlugs as readonly string[]).includes(sectionSlug)
  );
}

export function getOperationsSectionHref(
  projectId: string | number,
  sectionSlug: string
) {
  const area = getOperationsAreaForSection(sectionSlug);
  return area
    ? `/projects/${projectId}/operations/${area.id}/${sectionSlug}`
    : `/projects/${projectId}/operations/${sectionSlug}`;
}

/**
 * Server-issued drill surfaces that resolve to a registered operations
 * section route. Anything outside this allowlist has no canonical target and
 * must never be fabricated into a link by the frontend.
 */
const OPERATIONS_DRILL_SURFACES = new Set([
  "tickets",
  "submission-review",
  "planning-verification",
]);

/**
 * Query param name used when the server issues drill ids as a plain array
 * (improvement drill targets). Record-form drill ids keep their server keys.
 */
const OPERATIONS_DRILL_SURFACE_ID_PARAM: Record<string, string> = {
  tickets: "ticket",
  "submission-review": "submission",
  "planning-verification": "work",
};

/**
 * Resolve a server-issued drill target to a canonical, registered route.
 *
 * Unknown surfaces return null so renderers never build an unregistered URL.
 * Server-issued ids are serialized verbatim as the destination's query
 * parameters (never substituted with the parent item/metric id).
 */
export function resolveOperationsDrillHref(
  projectId: string | number,
  surface: string,
  ids: Record<string, string> | string[] | undefined
): string | null {
  if (!OPERATIONS_DRILL_SURFACES.has(surface)) return null;
  const params = new URLSearchParams();
  if (Array.isArray(ids)) {
    const paramKey = OPERATIONS_DRILL_SURFACE_ID_PARAM[surface] ?? "id";
    ids.forEach((id) => params.append(paramKey, id));
  } else if (ids) {
    Object.entries(ids).forEach(([key, value]) => params.set(key, value));
  }
  const base = getOperationsSectionHref(projectId, surface);
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}
